import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Box, IconButton, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { forwardRef } from 'react';

// Modern card-style toast — M3 surface background, colored 4px left
// accent stripe + colored icon, elevated shadow.  Replaces notistack's
// flat-fill default so toasts read as part of the card theme instead
// of attention-grabbing banners.
const VARIANT_STYLES = {
  success: { color: 'success.main', Icon: CheckCircleRoundedIcon },
  error: { color: 'error.main', Icon: ErrorRoundedIcon },
  warning: { color: 'warning.main', Icon: WarningRoundedIcon },
  info: { color: 'info.main', Icon: InfoRoundedIcon },
  default: { color: 'text.primary', Icon: InfoRoundedIcon },
};

function ToastContentBase({ id, message, variant, title, dismissible = true }, ref) {
  const { closeSnackbar } = useSnackbar();
  const { color, Icon } = VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;

  return (
    <Box
      ref={ref}
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        minWidth: 280,
        maxWidth: 420,
        bgcolor: 'var(--card-bg)',
        color: 'text.primary',
        border: '1px solid var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: 4, bgcolor: color, flexShrink: 0 }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          px: 'var(--space-3)',
          py: 'var(--space-2)',
          flex: 1,
          minWidth: 0,
        }}
      >
        <Icon sx={{ color, fontSize: 20, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title ? (
            <Typography variant="body2" sx={{ fontWeight: 'var(--weight-semibold)', lineHeight: 1.4 }}>
              {title}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
            {message}
          </Typography>
        </Box>
        {dismissible !== false ? (
          <IconButton
            size="small"
            aria-label="Dismiss"
            onClick={() => closeSnackbar(id)}
            sx={{ ml: 0.5, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}

const ToastContent = forwardRef(ToastContentBase);
ToastContent.displayName = 'ToastContent';

export default ToastContent;
